import { initializeApp } from "firebase/app";
import {
  getFirestore,
  enableIndexedDbPersistence,
  doc,
  collection,
  onSnapshot,
  writeBatch,
} from "firebase/firestore";
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { getStorage } from "firebase/storage";
import type { Analytics } from "firebase/analytics";

const rawEnv = import.meta.env as unknown as Record<string, unknown>;
const getEnvString = (key: string): string => {
  const value = rawEnv[key];
  return typeof value === "string" ? value : "";
};

const firebaseConfig = {
  apiKey: getEnvString("VITE_FIREBASE_API_KEY"),
  authDomain: getEnvString("VITE_FIREBASE_AUTH_DOMAIN"),
  projectId: getEnvString("VITE_FIREBASE_PROJECT_ID"),
  storageBucket: getEnvString("VITE_FIREBASE_STORAGE_BUCKET"),
  messagingSenderId: getEnvString("VITE_FIREBASE_MESSAGING_SENDER_ID"),
  appId: getEnvString("VITE_FIREBASE_APP_ID"),
  measurementId: getEnvString("VITE_FIREBASE_MEASUREMENT_ID"),
};

export const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const auth = getAuth(app);
export const storage = getStorage(app);
export let analytics: Analytics | null = null;

export const initializeAnalytics = async (): Promise<Analytics | null> => {
  if (
    analytics ||
    typeof window === "undefined" ||
    !firebaseConfig.measurementId
  ) {
    return analytics;
  }

  try {
    const { getAnalytics, isSupported } = await import("firebase/analytics");
    const supported = await isSupported().catch(() => false);

    if (!supported) {
      return null;
    }

    analytics = getAnalytics(app);
    return analytics;
  } catch (error) {
    console.warn("Analytics initialization skipped:", error);
    return null;
  }
};

// Export Firestore functions
export { doc, collection, onSnapshot, writeBatch };

// Enable offline persistence
enableIndexedDbPersistence(db).catch((err) => {
  const errorCode =
    typeof err === "object" &&
    err !== null &&
    "code" in err &&
    typeof (err as { code?: unknown }).code === "string"
      ? (err as { code: string }).code
      : "";

  if (errorCode === "failed-precondition") {
    console.warn("Firestore persistence disabled: multiple tabs open");
  } else if (errorCode === "unimplemented") {
    console.warn("Firestore persistence not supported in this browser");
  }
});

// Initialize auth persistence
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error("Error setting auth persistence:", error);
});
